import React, { useState } from "react";
import Datatable from "./common/datatable";
import FallbackImage from "./common/fallbackImage";
import { Eye, Trash2, ChevronDown } from "lucide-react";
import { useViewSingleUser } from "../hooks/users/viewSingleUser";
import { toast } from "react-hot-toast";

/* ─────────────────────────────────────────
   Resolve salesUserId from enquiry row
───────────────────────────────────────── */
const getSalesUserId = (row) =>
  row?.salesUserId?._id ||
  row?.salesUserId?.id ||
  (typeof row?.salesUserId === "string" ? row.salesUserId : null);

const getSalesUserName = (row) =>
  row?.salesUserId?.name ||
  row?.salesUserId?.email ||
  row?.name ||
  "Unknown";

/* ─────────────────────────────────────────
   Avatar — fetches profile picture by userId
───────────────────────────────────────── */
const SalesUserAvatar = ({ userId, name }) => {
  const { data } = useViewSingleUser(userId);

  // normalise response shape
  const user =
    data?.data?.user ||
    data?.data ||
    data?.user ||
    data ||
    null;

  const src =
    user?.profilePicture ||
    user?.profilePicturePath ||
    user?.avatar ||
    null;

  const initial = String(name || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  if (src) {
    return (
      <FallbackImage
        src={src}
        alt={name}
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <span
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: "#ede9fe",
        color: "#5c308d",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 14,
        flexShrink: 0,
      }}
    >
      {initial}
    </span>
  );
};

/* ─────────────────────────────────────────
   Status Selector Component
───────────────────────────────────────── */
const StatusSelector = ({ value, onChange, onSaving }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const statuses = ["pending", "closed"];

  const statusColor = {
    pending: "#f59e0b",
    closed: "#6b7280",
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === value) {
      setIsOpen(false);
      return;
    }
    setIsSaving(true);
    try {
      await onChange(newStatus);
      setIsOpen(false);
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ position: "relative", display: "inline-block", width: "max-content" }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSaving}
        style={{
          width: "100%",
          padding: "8px 12px",
          borderRadius: "6px",
          border: `1.5px solid ${statusColor[value] || "#d1d5db"}`,
          background: `${statusColor[value] || "#f3f4f6"}22`,
          color: statusColor[value] || "#6b7280",
          fontSize: "13px",
          fontWeight: "600",
          cursor: isSaving ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          transition: "all 0.2s",
          textTransform: "capitalize",
        }}
      >
        <span>{isSaving ? "Updating..." : value}</span>
        <ChevronDown size={16} style={{ opacity: 0.6 }} />
      </button>

      {isOpen && !isSaving && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: "4px",
            borderRadius: "6px",
            border: "1px solid #e5e7eb",
            background: "#fff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            zIndex: 10,
            overflow: "hidden",
          }}
        >
          {statuses.map((status) => (
            <button
              key={status}
              onClick={() => handleStatusChange(status)}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "none",
                background: value === status ? `${statusColor[status]}11` : "transparent",
                color: statusColor[status],
                fontSize: "13px",
                fontWeight: value === status ? "600" : "500",
                cursor: "pointer",
                textAlign: "left",
                textTransform: "capitalize",
                transition: "background 0.15s",
                borderLeft: value === status ? `3px solid ${statusColor[status]}` : "3px solid transparent",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `${statusColor[status]}22`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background =
                  value === status ? `${statusColor[status]}11` : "transparent";
              }}
            >
              {status}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const EnquiryData = ({ data, onView, onEdit, onDelete, onStatusChange }) => {
  const columns = [
    /* 1 ── NAME + AVATAR */
    {
      key: "salesUserId",
      label: "Name",
      render: (_, row) => {
        const userId = getSalesUserId(row);
        const name = getSalesUserName(row);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <SalesUserAvatar userId={userId} name={name} />
            <span style={{ fontWeight: 500, fontSize: 14 }}>{name}</span>
          </div>
        );
      },
    },

    /* 2 ── DATE */
    {
      key: "createdAt",
      label: "Date",
      render: (value) =>
        value
          ? new Date(value).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          : "-",
    },

    /* 3 ── STATUS */
    {
      key: "status",
      label: "Status",
      render: (value, row) => (
        <StatusSelector
          value={value || "pending"}
          onChange={(newStatus) =>
            onStatusChange && onStatusChange(row, newStatus)
          }
        />
      ),
    },

    /* 4 ── ACTIONS */
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button title="View" onClick={() => onView && onView(row)} style={actionBtn("#5c308d")}>
            <Eye size={15} />
          </button>
          <button title="Delete" onClick={() => onDelete && onDelete(row)} style={actionBtn("#dc2626")}>
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return <Datatable data={data} columns={columns} />;
};

const actionBtn = (color) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 30,
  height: 30,
  borderRadius: 6,
  border: `1px solid ${color}22`,
  background: `${color}11`,
  color,
  cursor: "pointer",
  transition: "background 0.15s",
  flexShrink: 0,
});

export default EnquiryData;
